(function () {
  if (window.ProductCardController) {
    return;
  }

  class CartBubbleMirrors {
    constructor() {
      this.masterObserver = null;
      this.syncTimeout = null;
      this.lastCount = null;
      this.ensureInitialized();
    }

    ensureInitialized() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initialize(), { once: true });
      } else {
        this.initialize();
      }
    }

    initialize() {
      this.observeMaster();
      this.sync();
      // Use event-based approach instead of observing entire body
      document.addEventListener('shopify:cart:update', () => this.sync());
      document.addEventListener('shopify:section:load', () => {
        setTimeout(() => {
          this.observeMaster();
          this.sync();
        }, 100);
      });
    }

    get master() {
      return document.getElementById('cart-icon-bubble');
    }

    observeMaster() {
      if (this.masterObserver) {
        this.masterObserver.disconnect();
        this.masterObserver = null;
      }
      const bubble = this.master;
      if (!bubble) return;
      
      // Debounced sync to prevent infinite loops
      const debouncedSync = () => {
        if (this.syncTimeout) clearTimeout(this.syncTimeout);
        this.syncTimeout = setTimeout(() => this.sync(), 50);
      };
      
      this.masterObserver = new MutationObserver(debouncedSync);
      this.masterObserver.observe(bubble, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-cart-count', 'class'],
        characterData: true,
      });
      this.sync();
    }

    sync() {
      if (this.syncTimeout) clearTimeout(this.syncTimeout);
      
      const bubble = this.master;
      const countValue = bubble ? bubble.getAttribute('data-cart-count') || bubble.textContent.trim() : '0';
      const parsed = parseInt(countValue, 10);
      const count = Number.isFinite(parsed) ? parsed : 0;

      // Skip if count hasn't changed
      if (this.lastCount === count) return;
      this.lastCount = count;

      document.querySelectorAll('[data-cart-bubble-mirror]').forEach((mirror) => {
        if (!mirror) return;
        if (count > 0) {
          mirror.textContent = String(count);
          mirror.classList.remove('hidden');
          mirror.setAttribute('aria-hidden', 'false');
        } else {
          const initial = mirror.getAttribute('data-initial-count') || '';
          mirror.textContent = initial === '0' ? '' : initial;
          mirror.classList.add('hidden');
          mirror.setAttribute('aria-hidden', 'true');
        }
      });
    }

    refresh() {
      this.observeMaster();
    }
  }

  class ProductCardController {
    constructor() {
      this.selector = '.product-add-to-cart';
      this.bubbleMirrors = new CartBubbleMirrors();
      this.latestCart = null;
      this.cartInitPromise = null;
      this.registerEvents();
      this.refreshButtonsFromDataset();
      this.initializeFromCart();
      document.addEventListener('shopify:section:load', () => this.refreshButtonsFromDataset());
      document.addEventListener('shopify:section:unload', () => this.refreshButtonsFromDataset());
    }

    registerEvents() {
      document.addEventListener('click', (event) => {
        const button = event.target.closest(this.selector);
        if (!button) return;
        event.preventDefault();
        if (button.disabled || button.getAttribute('aria-busy') === 'true') return;
        this.handleAddToCart(button);
      });
    }

    refreshButtonsFromDataset() {
      document.querySelectorAll(this.selector).forEach((button) => {
        const quantity = Number(button.dataset.cartQuantity || 0);
        this.updateAvailability(button, quantity);
      });

      if (this.latestCart) {
        this.updateButtonsWithCart(this.latestCart);
      } else {
        this.initializeFromCart();
      }
    }

    initializeFromCart() {
      if (this.cartInitPromise) return this.cartInitPromise;
      const initialCart = window.initialCart;
      if (initialCart) {
        this.updateButtonsWithCart(initialCart);
        return Promise.resolve(initialCart);
      }

      // Only fetch if we don't have initial cart data
      // Use requestIdleCallback for non-critical initialization
      if (window.requestIdleCallback) {
        this.cartInitPromise = new Promise((resolve) => {
          window.requestIdleCallback(() => {
            this.fetchCartState()
              .then((cart) => {
                if (cart) {
                  this.updateButtonsWithCart(cart);
                }
                resolve(cart);
              })
              .finally(() => {
                this.cartInitPromise = null;
              });
          });
        });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => {
          this.cartInitPromise = this.fetchCartState()
            .then((cart) => {
              if (cart) {
                this.updateButtonsWithCart(cart);
              }
              return cart;
            })
            .finally(() => {
              this.cartInitPromise = null;
            });
        }, 100);
      }

      return this.cartInitPromise || Promise.resolve(null);
    }

    setLoading(button, isLoading) {
      const spinner = button.querySelector('.product-add-to-cart__spinner');
      const content = button.querySelector('.product-add-to-cart__content');
      const label = button.querySelector('.product-add-to-cart__label');

      if (isLoading) {
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
        if (spinner) spinner.classList.remove('hidden');
        if (content) content.classList.add('opacity-0');
        if (label && button.dataset.loadingLabel) {
          label.dataset.previousText = label.textContent;
          label.textContent = button.dataset.loadingLabel;
        }
      } else {
        button.removeAttribute('aria-busy');
        if (spinner) spinner.classList.add('hidden');
        if (content) content.classList.remove('opacity-0');
        if (label && label.dataset.previousText) {
          label.textContent = label.dataset.previousText;
          delete label.dataset.previousText;
        }
      }
    }

    async handleAddToCart(button) {
      const variantId = button.dataset.productId;
      if (!variantId) return;
      this.setLoading(button, true);

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
          }),
        });

        const payload = await response.json();
        if (!response.ok || payload.status) {
          const message = payload.message || payload.description || button.dataset.errorText || 'Kunde inte lägga till produkten i varukorgen. Försök igen.';
          throw new Error(message);
        }

        await this.refreshCartSections();
        const cartState = await this.fetchCartState();
        if (cartState) {
          this.updateButtonsWithCart(cartState);
        }
        this.bubbleMirrors.sync();
      } catch (error) {
        console.error('[ProductCard] Unable to add to cart', error);
        alert(button.dataset.errorText || error.message || 'Kunde inte lägga till produkten i varukorgen. Försök igen.');
      } finally {
        this.setLoading(button, false);
      }
    }

    async refreshCartSections() {
      const sections = [];
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) sections.push('cart-drawer');
      sections.push('cart-icon-bubble');

      return Promise.all(
        sections.map(async (id) => {
          try {
            const response = await fetch(`/?section_id=${id}`);
            if (!response.ok) return;
            const html = await response.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            if (id === 'cart-drawer') {
              this.updateCartDrawer(doc);
            } else if (id === 'cart-icon-bubble') {
              this.replaceCartBubble(doc);
            }
          } catch (error) {
            console.error(`[ProductCard] Failed to refresh section ${id}`, error);
          }
        })
      );
    }

    updateCartDrawer(doc) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) return;
      const section = doc.querySelector('.shopify-section');
      if (!section) return;

      if (typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents({ sections: { 'cart-drawer': section.outerHTML } });
      } else {
        const newItems = section.querySelector('cart-drawer-items');
        const currentItems = cartDrawer.querySelector('cart-drawer-items');
        if (newItems && currentItems) {
          currentItems.replaceWith(newItems);
        }
      }

      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    }

    replaceCartBubble(doc) {
      const newBubble = doc.getElementById('cart-icon-bubble');
      const currentBubble = document.getElementById('cart-icon-bubble');
      if (newBubble && currentBubble) {
        currentBubble.replaceWith(newBubble);
        this.bubbleMirrors.refresh();
      }
    }

    async fetchCartState() {
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) throw new Error('Cart fetch failed');
        return await response.json();
      } catch (error) {
        console.error('[ProductCard] Unable to fetch cart state', error);
        return null;
      }
    }

    normalizeCartItems(cart) {
      if (!cart) return [];
      if (Array.isArray(cart.items)) return cart.items;
      if (Array.isArray(cart.line_items)) return cart.line_items;
      return [];
    }

    updateButtonsWithCart(cart) {
      if (!cart) return;
      const items = this.normalizeCartItems(cart);
      const quantities = new Map();
      items.forEach((item) => {
        const variantId = item.variant_id || item.variant?.id;
        if (!variantId) return;
        quantities.set(String(variantId), item.quantity);
      });

      this.latestCart = cart;
      window.initialCart = cart;

      document.querySelectorAll(this.selector).forEach((button) => {
        const variantId = button.dataset.productId;
        const quantity = quantities.get(String(variantId)) || 0;
        button.dataset.cartQuantity = quantity;
        this.updateAvailability(button, quantity);
      });
    }

    updateAvailability(button, cartQuantity) {
      const inventoryPolicy = button.dataset.inventoryPolicy || 'continue';
      const inventoryManagement = button.dataset.inventoryManagement || '';
      const inventoryQuantity = Number(button.dataset.inventoryQuantity || 0);
      const defaultLabel = button.dataset.defaultLabel;
      const soldOutLabel = button.dataset.soldOutLabel || defaultLabel;
      const maxLabel = button.dataset.maxLabel || soldOutLabel;
      const labelEl = button.querySelector('.product-add-to-cart__label');
      const variantAvailable = button.dataset.variantAvailable !== 'false';
      const tracksInventory = inventoryPolicy === 'deny' && inventoryManagement !== '';
      const noStock = tracksInventory && inventoryQuantity <= 0;
      const maxReached = tracksInventory && inventoryQuantity > 0 && cartQuantity >= inventoryQuantity;

      if (!variantAvailable || noStock) {
        button.disabled = true;
        if (labelEl) labelEl.textContent = soldOutLabel;
        return;
      }

      if (maxReached) {
        button.disabled = true;
        if (labelEl) labelEl.textContent = maxLabel;
        return;
      }

      button.disabled = false;
      if (labelEl && defaultLabel) {
        labelEl.textContent = defaultLabel;
      }
    }
  }

  window.ProductCardController = new ProductCardController();
})();
